// LevelIconPicker — a tone-tinted tile showing the current level icon; clicking
// opens a Dropdown grid of curated lucide icons. Purely presentational: the
// parent owns the selected value and receives the pick.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon } from '@/components/common';
import type { LevelTone } from '@/mocks/structure';

// Curated, verified lucide names appropriate for org-structure tiers.
export const LEVEL_ICON_CHOICES = [
  'git-branch',
  'briefcase',
  'layers',
  'users-round',
  'building-2',
  'network',
  'factory',
  'store',
  'warehouse',
  'map-pin',
  'box',
  'boxes',
  'landmark',
  'house',
  'flag',
  'anchor',
  'ship',
  'truck',
  'folder-tree',
  'users',
];

interface LevelIconPickerProps {
  value: string;
  tone: LevelTone;
  onChange: (icon: string) => void;
}

const LevelIconPicker = ({ value, tone, onChange }: LevelIconPickerProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const pick = (icon: string) => {
    onChange(icon);
    setOpen(false);
  };

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align='start'
      className='levels-icon-picker'
      trigger={
        <button
          type='button'
          className='levels-icon-picker__tile'
          aria-label={t('levels_pick_icon')}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <Icon
            name={value}
            size={32}
            strokeWidth={2}
            className={`levels-icon-picker__tile__icon levels-icon-picker__tile__icon--${tone}`}
          />
          <Icon name='chevron-down' size={14} className='levels-icon-picker__caret' />
        </button>
      }
    >
      <div className='levels-icon-picker__grid'>
        {LEVEL_ICON_CHOICES.map((icon) => (
          <button
            key={icon}
            type='button'
            className={`levels-icon-picker__choice${icon === value ? ' is-selected' : ''}`}
            aria-label={icon}
            aria-pressed={icon === value}
            onClick={() => pick(icon)}
          >
            <Icon name={icon} size={18} />
          </button>
        ))}
      </div>
    </Dropdown>
  );
};

export default LevelIconPicker;
